import React, { useRef, useState, useCallback, useEffect } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useInView } from 'react-intersection-observer';
import { Section } from '@wix/da-a11y/pkg/components/headings/tagWrappers';
import { ThemeOverride, ThemeSurface } from '@wix/da-react-context/pkg/ThemeContext';
import noop from '../../../../utils/noop';
import { getDeviationEntityId } from '../../../../redux/normalizr/schemas/deviation';
import { useSubscriptionTier } from '../../../../utils/hooks/useSubscriptionTier';
import { getDeviationResourceType, getDeviationUrl } from '../../../_helpers';
import FeedCardHeader from '../FeedCardHeader';
import ExpandableWrapper from '../ExpandableWrapper';
import FeedCardActionBar from '../FeedCardActionBar';
import FeedCardCommentThread from '../FeedCardCommentThread';
import ScrollToNextPostButton from '../ScrollToNextPostButton';
import { POST_HEIGHT_TO_SHOW_SCROLL_TO_NEXT } from '../../constants';
import { BiData } from '@wix/da-bi/pkg/events';
import s from './FeedCardLayout.scss';
const FeedCardLayout = ({ deviation, extraActions, className, children, isExpandable, withFaveControl, withCommentControl, withWatchOrSubscribeButton, withCommentSuggestions, expansionButton, forceShowExpansionToggle, additionalActionbarActions, collapsedContentHeight, onLayoutReady = noop, showReportModal = noop, deleteDeviation = noop, showMobileSharePopup = noop, showGenericNotification = noop })=>{
    var _contentContainerRef_current, _deviation_author, _deviation_author1;
    const { t } = useTranslation();
    const { needsSubscription, isSubscribedButBlocked, subscriptionsTabUrl } = useSubscriptionTier(deviation);
    const actionBlockedBySubscription = needsSubscription || isSubscribedButBlocked;
    const [isCommentThreadOpen, setCommentThreadOpen] = useState(false);
    const openCommentThread = useCallback(()=>setCommentThreadOpen(true), []);
    // track when the overflow is measured so we can turn off disableScrollRestoration in duperbrowse
    const [isOverflowMeasured, setIsOverflowMeasured] = useState(false);
    const onOverflowMeasured = useCallback(()=>{
        setIsOverflowMeasured(true);
        onLayoutReady();
    }, [
        onLayoutReady
    ]);
    // if it's not expandable, signal that's it's already ready
    useEffect(()=>{
        !isExpandable && onOverflowMeasured();
    }, [
        isExpandable,
        onOverflowMeasured
    ]);
    // have a sticky button appear when a long enough post is expanded allowing
    // the user to skip over it, also have it disappear if the end of this post is
    // scrolled into view
    const endOfThisPostRef = useRef(null);
    const [inViewRef, endAlreadyInView] = useInView();
    const setEndOfThisPostRef = useCallback((node)=>{
        // sets '.current' as normal but also hijack because we need to call inViewRef
        endOfThisPostRef.current = node;
        inViewRef(node);
    }, [
        inViewRef
    ]);
    const contentContainerRef = useRef(null);
    const expandedPostHeight = ((_contentContainerRef_current = contentContainerRef.current) === null || _contentContainerRef_current === void 0 ? void 0 : _contentContainerRef_current.offsetHeight) || 0;
    const hasScrollToNextButton = isExpandable && expandedPostHeight >= POST_HEIGHT_TO_SHOW_SCROLL_TO_NEXT;
    var _deviation_author_useridUuid;
    return /*#__PURE__*/ React.createElement("div", {
        className: classnames(s['root'], 'ds-surface-secondary', actionBlockedBySubscription && s['in-unsubscribed-tier'], className)
    }, /*#__PURE__*/ React.createElement(ThemeOverride, {
        themeSurface: ThemeSurface.SECONDARY
    }, /*#__PURE__*/ React.createElement(FeedCardHeader, {
        className: s['header'],
        deviation: deviation,
        extraActions: extraActions,
        // scroll restoration must wait until the overflows are determined,
        // otherwise things shift around after the scroll
        disableScrollRestoration: !isOverflowMeasured,
        withWatchOrSubscribeButton: withWatchOrSubscribeButton,
        showReportModal: showReportModal,
        deleteDeviation: deleteDeviation
    }), /*#__PURE__*/ React.createElement(Section, null, isExpandable ? /*#__PURE__*/ React.createElement(ExpandableWrapper, {
        contentContainerRef: contentContainerRef,
        collapsedHeight: collapsedContentHeight,
        toggleButtonBiData: BiData({
            evid: 116,
            typeid: getDeviationResourceType(deviation),
            itemid: deviation.deviationId,
            creatorid: (_deviation_author_useridUuid = (_deviation_author = deviation.author) === null || _deviation_author === void 0 ? void 0 : _deviation_author.useridUuid) !== null && _deviation_author_useridUuid !== void 0 ? _deviation_author_useridUuid : ''
        }),
        toggleButton: expansionButton,
        overflowCacheKey: `feed-card-${getDeviationEntityId(deviation)}`,
        onExpansion: openCommentThread,
        onOverflowMeasured: onOverflowMeasured,
        allowExpansion: !actionBlockedBySubscription,
        forceShowToggle: forceShowExpansionToggle,
        toggleButtonContents: actionBlockedBySubscription ? t('deviationview.feed.subscribeToReadMore') : t('deviationview.feed.readmore'),
        toggleButtonHref: actionBlockedBySubscription ? subscriptionsTabUrl : getDeviationUrl(deviation)
    }, children) : children), /*#__PURE__*/ React.createElement("div", {
        className: s['footer']
    }, /*#__PURE__*/ React.createElement(FeedCardActionBar, {
        className: s['action-bar'],
        deviation: deviation,
        withFaveControl: withFaveControl,
        withCommentControl: withCommentControl,
        additionalActions: additionalActionbarActions,
        openCommentThread: openCommentThread,
        showMobileSharePopup: showMobileSharePopup
    }), !actionBlockedBySubscription && /*#__PURE__*/ React.createElement(FeedCardCommentThread, {
        deviation: deviation,
        isThreadOpen: isCommentThreadOpen,
        openCommentThread: openCommentThread,
        showGenericNotification: showGenericNotification,
        withCommentSuggestions: withCommentSuggestions
    })), hasScrollToNextButton && /*#__PURE__*/ React.createElement(ScrollToNextPostButton, {
        buttonClassName: classnames(s['scroll-to-next-button'], !endAlreadyInView && s['visible']),
        isDisplayed: hasScrollToNextButton,
        scrollTarget: endOfThisPostRef,
        biData: BiData({
            evid: 119,
            typeid: getDeviationResourceType(deviation),
            itemid: deviation.deviationId,
            creatorid: (_deviation_author1 = deviation.author) === null || _deviation_author1 === void 0 ? void 0 : _deviation_author1.useridUuid
        })
    }), hasScrollToNextButton && /*#__PURE__*/ React.createElement("div", {
        ref: setEndOfThisPostRef
    })));
};
FeedCardLayout.displayName = 'FeedCardLayout';
export default FeedCardLayout;

//# sourceMappingURL=FeedCardLayout.js.map